import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  getSurveySampleByIdApi,
  getSurveySampleDimensionsByIdApi,
  updateSurveyDimensionByIdApi,
  updateSurveySampleByIdApi,
} from '../../../api/survey.sample';
import { clearSurveyCacheApi } from '../../../api/surveys';
import { QUESTION_FILTER_TYPE } from '../../../constants/common';
import types from '../../../types';

// Owns the sample + dimensions of the question being edited. All state lives
// here; the ONLY redux interaction is mirrorToStore, because the shared Chart
// component renders the expanded question from state.surveySample.sample.
const useQuestionEditor = (jwt) => {
  const dispatch = useDispatch();
  const [sample, setSample] = useState();
  const [dimensions, setDimensions] = useState();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const mirrorToStore = useCallback(
    (nextSample, nextDimensions, sampleTheme = null) => {
      dispatch({
        type: types.reducerTypes.SURVEY_SAMPLE_DIMENSION_GET_SUCCESS,
        payload: { sample: nextSample, dimensions: nextDimensions, sampleTheme },
      });
    },
    [dispatch]
  );

  const loadQuestion = useCallback(
    async (sampleId) => {
      setLoading(true);
      setLoadError(null);
      try {
        const [sampleData, dimensionsData] = await Promise.all([
          getSurveySampleByIdApi(sampleId, jwt),
          getSurveySampleDimensionsByIdApi(sampleId, jwt),
        ]);
        setSample(sampleData);
        setDimensions(dimensionsData);
        mirrorToStore(sampleData, dimensionsData);
        return { sample: sampleData, dimensions: dimensionsData };
      } catch (e) {
        console.error(e);
        setLoadError(e);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [jwt, mirrorToStore]
  );

  const clearLoadError = useCallback(() => setLoadError(null), []);

  // Persists the edited question. When this question becomes the location
  // filter, the previous location-filter question is reset to NONE first.
  const saveQuestion = useCallback(
    async ({ samplePatch, updatedDimensions, surveyLocationFilter, surveyId }) => {
      setSaving(true);
      try {
        if (
          samplePatch.Data.filterType === QUESTION_FILTER_TYPE.LOCATION &&
          surveyLocationFilter &&
          surveyLocationFilter.id !== samplePatch.Id
        ) {
          await updateSurveySampleByIdApi(
            surveyLocationFilter.id,
            {
              Id: surveyLocationFilter.id,
              Data: { filterType: QUESTION_FILTER_TYPE.NONE },
            },
            jwt
          );
        }
        await Promise.all([
          updateSurveySampleByIdApi(samplePatch.Id, samplePatch, jwt),
          ...updatedDimensions.map((dimension) =>
            updateSurveyDimensionByIdApi(dimension.Id, dimension, jwt)
          ),
        ]);
        await clearSurveyCacheApi(surveyId, jwt);
        // Keep the local state (and the store mirror the Chart renders from)
        // in sync with what was just saved, so the form and preview never
        // fall back to the pre-save values while waiting for the backend
        // reload to come back.
        const mergedSample = sample
          ? { ...sample, Data: { ...sample.Data, ...samplePatch.Data } }
          : sample;
        setSample(mergedSample);
        setDimensions(updatedDimensions);
        if (mergedSample) {
          mirrorToStore(mergedSample, updatedDimensions);
        }
      } finally {
        setSaving(false);
      }
    },
    [jwt, sample, mirrorToStore]
  );

  const moveQuestionToSection = useCallback(
    async ({ questionId, sectionCode, identifier, surveyId }) => {
      setSaving(true);
      try {
        await updateSurveySampleByIdApi(
          questionId,
          {
            sampleId: questionId,
            Data: {
              sectionIdentifier: sectionCode,
              identifier,
            },
          },
          jwt
        );
        await clearSurveyCacheApi(surveyId, jwt);
      } finally {
        setSaving(false);
      }
    },
    [jwt]
  );

  return {
    sample,
    dimensions,
    loading,
    saving,
    loadError,
    clearLoadError,
    loadQuestion,
    saveQuestion,
    moveQuestionToSection,
    mirrorToStore,
  };
};

export default useQuestionEditor;
